import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Pencil } from "lucide-react";

const FIELDS = [["bank_name", "Bank name"], ["account_number", "Account number"], ["ifsc", "IFSC"], ["pan", "PAN"]];

const EditPayeeDialog = ({ row, onClose, onSaved }) => {
  const [f, setF] = useState(row.payee);
  const save = async () => {
    try {
      const body = Object.fromEntries(Object.entries(f).map(([k, v]) => [k, (v || "").trim()]).filter(([, v]) => v));
      await api.patch(`/v2/payees/${row.payee_id}`, body);
      toast.success("Bank details saved");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="bank-edit-dialog">
        <DialogHeader><DialogTitle>Bank details · {row.name}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {FIELDS.map(([k, label]) => (
            <div key={k}><Label>{label}</Label>
              <Input value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`bank-edit-${k}`} /></div>
          ))}
        </div>
        <DialogFooter><Button onClick={save} data-testid="bank-edit-save">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const BankMissingTab = () => {
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = async () => { const r = await api.get("/v2/payees/bank-missing"); setData(r.data); };
  useEffect(() => { load(); }, []);
  if (!data) return <div className="text-sm text-slate-500">Loading…</div>;
  return (
    <Card data-testid="bank-missing-tab">
      <CardContent className="p-3">
        <div className="text-sm text-slate-600 mb-2" data-testid="bank-missing-count">
          {data.count} active team member(s) are missing bank details. They can't be reimbursed until these are filled in.
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-left text-slate-500"><tr>{["Name", "Employee ID", "Projects", "Missing", ""].map((h) => <th key={h} className="p-2 font-medium">{h}</th>)}</tr></thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.user_id} className="border-t" data-testid={`bank-missing-row-${r.user_id}`}>
                  <td className="p-2">{r.name}<div className="text-[10px] text-slate-500">{r.email}</div></td>
                  <td className="p-2 font-mono">{r.employee_code || "—"}</td>
                  <td className="p-2">{r.projects.map((p) => <Badge key={p} variant="outline" className="mr-1 mb-1">{p}</Badge>)}</td>
                  <td className="p-2 text-rose-600">{r.missing.join(", ")}{r.pending_verification && <Badge className="ml-1 bg-amber-500 text-[9px]" data-testid={`bank-missing-pending-${r.user_id}`}>Submitted · awaiting verification</Badge>}</td>
                  <td className="p-2 text-right">
                    <Button size="sm" variant="outline" disabled={!r.payee_id} onClick={() => setEdit(r)} data-testid={`bank-missing-edit-${r.user_id}`}><Pencil className="w-3 h-3 mr-1" /> Edit</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {edit && <EditPayeeDialog row={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
      </CardContent>
    </Card>
  );
};
